import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Image as ImageIcon, Send } from "lucide-react";
import { useRef, useState } from "react";

interface MessageFormProps {
  newMessage: string;
  isLoading: boolean;
  onMessageChange: (message: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onImageUpload: (file: File) => void;
}

export const MessageForm = ({ 
  newMessage, 
  isLoading, 
  onMessageChange, 
  onSubmit,
  onImageUpload
}: MessageFormProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      setSelectedFileName(file.name);
      console.log("Selected file for upload:", file.name);
      
      onImageUpload(file);
      
      // Reset the input so the same file can be selected again
      setTimeout(() => {
        if (e.target) e.target.value = '';
        setUploading(false);
        // Keep the file name displayed until message is sent
      }, 500);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading || uploading) return;
    
    console.log("Submitting message form with message:", newMessage);
    onSubmit(e);
    // Clear the uploaded file name after sending
    setSelectedFileName(null);
  };

  return (
    <form onSubmit={handleFormSubmit} className="space-y-4">
      <div className="flex items-end gap-3">
        {/* Image Upload Button */}
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleImageButtonClick}
            disabled={isLoading || uploading}
            className="h-12 w-12 rounded-full bg-background/50 hover:bg-background/80 border-border/50 hover:border-primary/30 transition-all duration-200 hover:shadow-md"
          >
            {uploading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
            ) : (
              <ImageIcon className="h-5 w-5 text-muted-foreground" />
            )}
          </Button>
        </div>
        
        {/* Message Input */}
        <div className="flex-1 relative">
          <Input
            type="text"
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => onMessageChange(e.target.value)}
            disabled={isLoading || uploading}
            className="h-12 rounded-3xl bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 pl-4 pr-14 transition-all duration-200 text-base"
          />
          
          {/* Send Button - Positioned inside input */}
          <Button 
            type="submit" 
            disabled={isLoading || uploading || !newMessage.trim()}
            size="icon"
            className="absolute right-1 top-1 h-10 w-10 rounded-full bg-primary hover:bg-primary/90 transition-all duration-200 disabled:opacity-50"
          >
            {isLoading ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
      
      {/* Upload Status */}
      {selectedFileName && (
        <div className="flex items-center gap-2 text-sm text-primary bg-primary/5 border border-primary/20 p-3 rounded-xl">
          <ImageIcon className="h-4 w-4 text-primary" />
          <span>Ready to send: {selectedFileName}</span>
        </div>
      )}
    </form>
  );
};
